import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { DocumentItem } from "@/shared/ui/DocumentItem.jsx";
import { useGetRequest } from "@/shared/hooks/requests.js";
import { RECEIVED_SHARES, SENT_SHARES } from "@/shared/utils/urls.js";
import { Button } from "@/components/ui/button";
import { DeleteShareModal } from "./DeleteShare.jsx";
import { PropertiesModal } from "@/shared/ui/PropertiesModal.jsx";
import { tagShareItems } from "@/modules/shared/utils.js";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { Trash2, Eye, Pencil, Info, UserCog } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { canHandOver } from '@/shared/utils/nodeAccess.js';
import { TransferOwnerModal } from './TransferOwnerModal.jsx';

// The level a received share gives (older payloads carry only the legacy
// permission: viewer read and commented, so it is a commenter).
const levelOf = (share) => share?.level || (share?.permission === 'editor' ? 'editor' : share?.permission ? 'commenter' : null);

// The pill on a received card: the level, and when the access ends.
export const LevelPill = ({ share }) => {
  const { t } = useTranslation();
  const level = levelOf(share);
  if (!level) return null;
  const writes = level === 'editor' || level === 'manager' || level === 'contributor';
  const Icon = writes ? Pencil : Eye;
  const until = share?.expiresAt ? t('shareModal:access.expires.until', { date: new Date(share.expiresAt).toLocaleDateString() }) : null;
  return (
    <span
      className={`absolute top-1 right-1 z-10 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider shadow-sm ${
        writes ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' : 'bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-white/70'
      }`}
      title={[t(`shareModal:access.level.${level}.hint`), until].filter(Boolean).join(' · ')}
    >
      <Icon className="w-2.5 h-2.5" />
      {t(`shareModal:access.level.${level}.name`)}
    </span>
  );
};

export const SharedDocument = ({ file, owner }) => (
  <div className="relative w-fit">
    <LevelPill share={file} />
    <DocumentItem doc={file?.node} share owner={owner} />
  </div>
);

// A folder share rendered as a grid card — same folder visual as My Docs, and
// clickable to open the folder's contents (the recipient has access via the share).
export const SharedFolder = ({ share }) => {
  const { t } = useTranslation();
  const folder = share?.node;
  return (
    <Link
      to={`/files/${folder?.uuid}`}
      state={{ uuid: folder?.uuid }}
      className="relative w-[135px] flex flex-col items-center group cursor-pointer"
    >
      <LevelPill share={share} />
      <div className="w-[135px] h-[120px] flex items-center justify-center rounded-lg transition-all group-hover:bg-gray-50 group-hover:scale-105">
        <img src="/images/folder.svg" alt="Folder" className="w-20 h-20 object-contain" />
      </div>
      <span
        className="w-full text-center text-sm text-foreground truncate group-hover:text-blue-600 transition-colors"
        title={folder?.name}
      >
        {folder?.name || t('shared:untitledFolder')}
      </span>
    </Link>
  );
};

export const SharedTable = () => {
  const { t } = useTranslation();
  const [mode, setMode] = useState("received");
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedShare, setSelectedShare] = useState(null);
  const [propsOpen, setPropsOpen] = useState(false);
  const [propsResource, setPropsResource] = useState(null);
  const [propsType, setPropsType] = useState('file');
  // a received file or folder being handed over (console managers)
  const [handingOver, setHandingOver] = useState(null);

  const getReceived = useGetRequest({ url: RECEIVED_SHARES });
  const getSent = useGetRequest({ url: SENT_SHARES });

  const refresh = () => {
    if (mode === "sent") getSent.request();
    else getReceived.request();
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // One mixed list from the unified endpoint, each item type-tagged.
  const shares = (mode === "sent" ? getSent.response : getReceived.response) || [];
  const items = tagShareItems(shares);

  const openProperties = (item) => {
    setPropsResource(item.data.node);
    setPropsType(item.kind);
    setPropsOpen(true);
  };

  return (
    <div className="w-full h-full flex flex-col items-center p-5 gap-5 fade-slide-in">
      <div className="w-full flex justify-between items-center">
        <span className="text-black font-medium text-lg">{t('shared:title')}</span>
        <div className="flex gap-2">
          <Button variant={mode === "sent" ? "default" : "outline"} onClick={() => setMode("sent")}>
            {t('shared:btn.sent')}
          </Button>
          <Button variant={mode === "received" ? "default" : "outline"} onClick={() => setMode("received")}>
            {t('shared:btn.received')}
          </Button>
        </div>
      </div>

      <div
        key={mode}
        className="w-full grid grid-cols-[repeat(auto-fill,135px)] gap-x-6 gap-y-8 justify-between max-md:justify-evenly fade-slide-in"
      >
        {items.length > 0 ? (
          items.map((item) => (
            <ContextMenu key={item.key}>
              <ContextMenuTrigger>
                {item.kind === 'folder'
                  ? <SharedFolder share={item.data} />
                  : <SharedDocument file={item.data} owner={mode === 'sent'} />}
              </ContextMenuTrigger>
              <ContextMenuContent>
                <ContextMenuItem onClick={() => openProperties(item)}>
                  <Info className="mr-2 h-4 w-4" />
                  {t('my-docs:properties.title') || 'Properties'}
                </ContextMenuItem>
                {mode === "received" && canHandOver(item.data.node) && (
                  <ContextMenuItem onClick={() => setHandingOver({ ...item.data.node, type: item.kind })}>
                    <UserCog className="mr-2 h-4 w-4" />
                    {t('my-docs:owner.menu')}
                  </ContextMenuItem>
                )}
                {mode === "sent" && (
                  <ContextMenuItem
                    onClick={() => { setSelectedShare(item.data); setOpenDelete(true); }}
                  >
                    <Trash2 className="mr-2 h-4 w-4 text-red-600 focus:text-red-600" />
                    {t('shareModal:remove.btn')}
                  </ContextMenuItem>
                )}
              </ContextMenuContent>
            </ContextMenu>
          ))
        ) : (
          <div className="col-span-full flex items-center justify-center text-gray-500 h-40">
            {mode === "sent" ? `${t('shared:empty.sent')}` : `${t('shared:empty.received')}`}
          </div>
        )}
      </div>

      {mode === "sent" && (
        <DeleteShareModal
          setData={refresh}
          open={openDelete}
          setOpen={setOpenDelete}
          file={selectedShare}
        />
      )}

      <PropertiesModal
        open={propsOpen}
        setOpen={setPropsOpen}
        resource={propsResource}
        type={propsType}
      />

      <TransferOwnerModal
        open={Boolean(handingOver)}
        setOpen={(value) => { if (!value) setHandingOver(null); }}
        node={handingOver}
        onDone={refresh}
      />
    </div>
  );
};
